<template>
  <section class="plan-hero">
    <div class="plan-hero__inner">
      <div class="plan-hero__head">
        <div class="plan-hero__eyebrow">
          {{ eyebrow }}
        </div>
        <div class="plan-hero__title-row">
          <h1 class="plan-hero__title">
            {{ title }}
          </h1>
          <span
            v-if="badge"
            class="plan-hero__badge"
            :class="`plan-hero__badge--${badge.tone}`"
          >
            {{ badge.label }}
          </span>
        </div>
        <p
          v-if="subtitle"
          class="plan-hero__subtitle"
        >
          {{ subtitle }}
        </p>
      </div>

      <div class="plan-hero__meta">
        <div
          v-if="priceLabel"
          class="plan-hero__price"
        >
          <span
            v-if="catalogStrikeLabel"
            class="plan-hero__price-strike"
          >
            {{ catalogStrikeLabel }}
          </span>
          <span class="plan-hero__price-amount">{{ priceLabel }}</span>
          <span
            v-if="frequencyLabel"
            class="plan-hero__price-period"
          >
            / {{ frequencyLabel }}
          </span>
          <span
            v-if="plan.discountPercent"
            class="plan-hero__price-discount"
          >
            −{{ plan.discountPercent }}%
          </span>
        </div>

        <div
          v-if="nextRenewalAt"
          class="plan-hero__row"
        >
          <i class="fa-regular fa-calendar plan-hero__row-icon" />
          <span>
            {{ t('billing.subscriptionPage.hero.nextRenewal') }}
            <strong>{{ formattedNextRenewal }}</strong>
          </span>
        </div>

        <div
          v-if="metricStat"
          class="plan-hero__metric"
        >
          <span class="plan-hero__metric-icon">
            <i class="fa-regular fa-chart-line" />
          </span>
          <span class="plan-hero__metric-text">
            <span class="plan-hero__metric-value">{{ metricStat.value }}</span>
            <span class="plan-hero__metric-label">{{ metricStat.label }}</span>
            <span
              v-if="metricStat.nonBillable"
              class="plan-hero__metric-excluded"
            >{{ t('billing.subscriptionPage.hero.metricNonBillable', { count: metricStat.nonBillable }) }}</span>
          </span>
        </div>
        <button
          v-if="metricStat && metricStat.kind === 'contacts'"
          type="button"
          class="plan-hero__exclusion-link"
          @click="$emit('manage-auto-exclusion')"
        >
          <span class="plan-hero__exclusion-link-main">
            <i class="fa-regular fa-user-slash" />
            <span>{{ t('billing.autoExclusion.manageAction') }}</span>
          </span>
          <i class="fa-regular fa-chevron-right plan-hero__exclusion-link-arrow" />
        </button>
      </div>
    </div>

    <!-- Scheduled downgrade notice (only when nextPlan ≠ currentPlan, the
         backend has already filtered out the no-op case). -->
    <div
      v-if="plan.nextPlan"
      class="plan-hero__next-plan"
    >
      <div class="plan-hero__next-plan-icon">
        <i class="fa-regular fa-arrow-down-arrow-up" />
      </div>
      <div class="plan-hero__next-plan-body">
        <div class="plan-hero__next-plan-eyebrow">
          {{ t('billing.subscriptionPage.hero.nextPlanEyebrow') }}
        </div>
        <div class="plan-hero__next-plan-text">
          {{ t('billing.subscriptionPage.hero.nextPlan', {
            current: plan.name,
            next: nextPlanName,
            date: formattedNextRenewal,
          }) }}
        </div>
      </div>
      <button
        type="button"
        class="plan-hero__next-plan-undo"
        @click="$emit('undo-downgrade')"
      >
        <i class="fa-regular fa-rotate-left" />
        <span>{{ t('billing.subscriptionPage.actions.undoDowngrade') }}</span>
      </button>
    </div>

    <div class="plan-hero__actions">
      <SpmButton
        v-if="status === 'none' || status === 'cancelled'"
        :label="t('billing.subscriptionPage.actions.choosePlan')"
        icon="fa-regular fa-arrow-right"
        icon-pos="right"
        class-style="plan-hero__primary p-button"
        @click="$emit('change-plan')"
      />
      <SpmButton
        v-else-if="status === 'trial' || status === 'trial_expiring_soon'"
        :label="t('billing.subscriptionPage.actions.subscribeDuringTrial')"
        icon="fa-regular fa-arrow-right"
        icon-pos="right"
        class-style="plan-hero__primary p-button"
        @click="$emit('change-plan')"
      />
      <SpmButton
        v-else-if="canUpgrade"
        :label="t('billing.subscriptionPage.actions.changePlan')"
        icon="fa-regular fa-arrows-rotate"
        class-style="plan-hero__primary p-button"
        @click="$emit('change-plan')"
      />
      <SpmButton
        v-if="plan.eligibleToExtendTrial"
        :label="t('billing.subscriptionPage.actions.extendTrial')"
        icon="fa-regular fa-clock"
        class-style="plan-hero__primary p-button"
        @click="$emit('extend-trial')"
      />
      <Button
        v-if="canCancel && !isCancelScheduled"
        :label="t('billing.subscriptionPage.actions.cancel')"
        icon="fa-regular fa-xmark"
        class="p-button-text plan-hero__secondary"
        @click="$emit('cancel')"
      />
      <Button
        v-if="isCancelScheduled"
        :label="t('billing.subscriptionPage.actions.undoCancel')"
        icon="fa-regular fa-rotate-left"
        class="p-button-text plan-hero__secondary"
        @click="$emit('undo-cancel')"
      />
    </div>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import moment from 'moment';
import { PlanSection, PlanStatus } from '@/types/billing-types';

export default defineComponent({
  name: 'PlanHeroCard',

  components: { Button, SpmButton },

  props: {
    plan: {
      type: Object as PropType<PlanSection>,
      required: true,
    },
  },

  emits: [
    'change-plan',
    'cancel',
    'undo-cancel',
    'extend-trial',
    'undo-downgrade',
    'manage-auto-exclusion',
  ],

  setup(props) {
    const { t } = useI18n();

    const status = computed(() => props.plan.status);
    const isCancelScheduled = computed(() => props.plan.isCancelScheduled);
    const canCancel = computed(() => props.plan.canCancel);
    const canUpgrade = computed(() => props.plan.canUpgrade);

    const nextPlanName = computed(() => {
      if (!props.plan.nextPlan) return '';
      // Reuse the i18n keys for plan names (billing.compare.planName.<code>).
      return t(`billing.compare.planName.${props.plan.nextPlan}`);
    });

    const eyebrow = computed(() => {
      switch (props.plan.status) {
        case 'none':
        // `cancelled` = had a plan, now `is_active = 0`. Same UX as
        // `none` here: no offer, the user must choose one. Without
        // this case the eyebrow falls to the default "OFFRE
        // ACTUELLE" which is misleading.
        case 'cancelled':
          return t('billing.subscriptionPage.hero.eyebrowNone');
        case 'trial':
        case 'trial_expiring_soon':
          return t('billing.subscriptionPage.hero.eyebrowTrial');
        case 'pending_payment':
        case 'pending_capped_increase':
          return t('billing.subscriptionPage.hero.eyebrowPending');
        default:
          return t('billing.subscriptionPage.hero.eyebrowCurrent');
      }
    });

    const title = computed(() => (
      props.plan.name ?? t('billing.subscriptionPage.hero.noPlanTitle')
    ));

    const subtitle = computed(() => {
      if (props.plan.status === 'none' || props.plan.status === 'cancelled') {
        return t('billing.subscriptionPage.hero.subtitleNoPlan');
      }
      // Trial countdown — defensive: also covers the case where the
      // backend didn't populate `daysUntilTrialEnd` but the status is
      // clearly a trial one. Without the fallback the user gets a string
      // like "Plus que  jour(s)..." (no number) which looks like a bug.
      if (
        props.plan.status === 'trial'
        || props.plan.status === 'trial_expiring_soon'
        || props.plan.statusContext?.daysUntilTrialEnd !== undefined
      ) {
        const raw = props.plan.statusContext?.daysUntilTrialEnd;
        const days = Number.isFinite(raw as number)
          ? Math.max(0, Number(raw))
          : 0;
        return t('billing.subscriptionPage.hero.subtitleTrial', { days });
      }
      if (props.plan.status === 'unsubscribe' && props.plan.nextRenewalAt) {
        return t('billing.subscriptionPage.hero.subtitleUnsubscribe', {
          date: moment(props.plan.nextRenewalAt).format('DD/MM/YYYY'),
        });
      }
      return '';
    });

    const badge = computed(() => {
      const s = props.plan.status;
      const map: Partial<Record<PlanStatus, { label: string; tone: string }>> = {
        trial: { label: t('billing.subscriptionPage.hero.badge.trial'), tone: 'info' },
        trial_expiring_soon: {
          label: t('billing.subscriptionPage.hero.badge.trialEnding'),
          tone: 'warning',
        },
        active: { label: t('billing.subscriptionPage.hero.badge.active'), tone: 'success' },
        unsubscribe: {
          label: t('billing.subscriptionPage.hero.badge.cancelling'),
          tone: 'warning',
        },
        pending_payment: {
          label: t('billing.subscriptionPage.hero.badge.pending'),
          tone: 'warning',
        },
        pending_capped_increase: {
          label: t('billing.subscriptionPage.hero.badge.pending'),
          tone: 'warning',
        },
        payment_failed_first: {
          label: t('billing.subscriptionPage.hero.badge.unpaid'),
          tone: 'danger',
        },
        payment_failed_second: {
          label: t('billing.subscriptionPage.hero.badge.unpaid'),
          tone: 'danger',
        },
      };
      return map[s] ?? null;
    });

    const priceLabel = computed(() => (
      props.plan.pricePaid?.label
        ?? props.plan.price?.label
        ?? null
    ));

    const frequencyLabel = computed(() => {
      if (props.plan.frequency === 'yearly') return t('billing.subscriptionPage.hero.year');
      if (props.plan.frequency === 'monthly') return t('billing.subscriptionPage.hero.month');
      return null;
    });

    const nextRenewalAt = computed(() => props.plan.nextRenewalAt);
    const formattedNextRenewal = computed(() => (
      nextRenewalAt.value ? moment(nextRenewalAt.value).format('DD MMMM YYYY') : '—'
    ));

    // Headline usage stat shown in the hero: total pages viewed (v3/v4) or
    // total active contacts (v5). Driven by plan.metricTotal which the
    // backend computes for every offer version.
    const metricStat = computed(() => {
      const m = props.plan.metricTotal;
      if (!m) return null;
      return {
        kind: m.kind,
        value: new Intl.NumberFormat('fr-FR').format(m.value),
        label:
          m.kind === 'contacts'
            ? t('billing.subscriptionPage.hero.metricContacts')
            : t('billing.subscriptionPage.hero.metricPagesViewed'),
        // V5 : nombre de contacts non-facturables (is_billable=0) — complément
        // des contacts actifs, cohérent avec le badge de « Mes contacts ».
        nonBillable:
          m.kind === 'contacts' && m.nonBillable && m.nonBillable > 0
            ? new Intl.NumberFormat('fr-FR').format(m.nonBillable)
            : null,
      };
    });

    // Catalog price shown struck-through when the shop actually pays less than
    // the catalog price (negotiated discount / plan_price ceiling / proration).
    const catalogStrikeLabel = computed<string | null>(() => {
      const { price, pricePaid } = props.plan;
      if (price && pricePaid && pricePaid.amount < price.amount) {
        return price.label;
      }
      return null;
    });

    return {
      t,
      status,
      isCancelScheduled,
      canCancel,
      canUpgrade,
      nextPlanName,
      eyebrow,
      title,
      subtitle,
      badge,
      priceLabel,
      frequencyLabel,
      nextRenewalAt,
      formattedNextRenewal,
      metricStat,
      catalogStrikeLabel,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.plan-hero {
  position: relative;
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  color: #fff;
  border-radius: 16px;
  padding: 32px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 12px 32px -8px rgba(0, 0, 0, 0.08);

  &::before {
    content: "";
    position: absolute;
    top: -120px;
    right: -120px;
    width: 320px;
    height: 320px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(148, 200, 64, 0.32) 0%, transparent 70%);
    pointer-events: none;
  }

  &__inner {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }

  &__head {
    min-width: 0;
  }

  &__eyebrow {
    font-size: 0.75rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 600;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 6px;
  }

  &__title {
    font-size: 2rem;
    font-weight: 700;
    margin: 0;
    line-height: 1.1;
    letter-spacing: -0.02em;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.02em;

    &--success {
      background: rgba(148, 200, 64, 0.18);
      color: #c1e07e;
    }
    &--info {
      background: rgba(96, 165, 250, 0.18);
      color: #93c5fd;
    }
    &--warning {
      background: rgba(250, 183, 16, 0.18);
      color: #fcd34d;
    }
    &--danger {
      background: rgba(239, 68, 68, 0.18);
      color: #fca5a5;
    }
  }

  &__subtitle {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 8px 0 0;
    line-height: 1.5;
  }

  &__meta {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__price {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }

  &__price-amount {
    font-size: 2rem;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.02em;
  }

  &__price-period {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.78);

    strong {
      color: #fff;
      font-weight: 600;
    }
  }

  &__row-icon {
    width: 16px;
    color: rgba(255, 255, 255, 0.5);
  }

  // ─── Catalog price strike-through + discount badge ───────────────
  &__price-strike {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.45);
    text-decoration: line-through;
  }

  &__price-discount {
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border-radius: 100px;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    background: rgba(148, 200, 64, 0.18);
    color: #c1e07e;
  }

  // ─── Headline usage metric (pages viewed / active contacts) ──────
  &__metric {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 4px;
    padding: 12px 14px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
  }

  &__metric-icon {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(148, 200, 64, 0.16);
    color: #c1e07e;
    font-size: 0.85rem;
  }

  &__metric-text {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
    min-width: 0;
  }

  &__metric-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.01em;
  }

  &__metric-label {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
  }

  &__metric-excluded {
    font-size: 0.6875rem;
    color: rgba(255, 255, 255, 0.55);
    margin-top: 2px;
  }

  &__exclusion-link {
    appearance: none;
    cursor: pointer;
    margin-top: 10px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: rgba(255, 255, 255, 0.88);
    font-size: 0.8125rem;
    font-weight: 600;
    padding: 10px 14px;
    border-radius: 10px;
    transition: background 0.15s ease, border-color 0.15s ease;

    &-main {
      display: inline-flex;
      align-items: center;
      gap: 9px;

      i {
        font-size: 0.875rem;
        opacity: 0.75;
      }
    }

    &-arrow {
      font-size: 0.75rem;
      opacity: 0.5;
      transition: transform 0.15s ease, opacity 0.15s ease;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(255, 255, 255, 0.26);

      .plan-hero__exclusion-link-arrow {
        transform: translateX(3px);
        opacity: 0.85;
      }
    }
  }

  &__actions {
    position: relative;
    z-index: 1;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 24px;
  }

  &__next-plan {
    position: relative;
    z-index: 1;
    margin-top: 20px;
    display: flex;
    align-items: center;
    gap: 14px;
    background: linear-gradient(135deg, rgba(250, 183, 16, 0.14) 0%, rgba(250, 183, 16, 0.04) 100%);
    border: 1px solid rgba(250, 183, 16, 0.32);
    border-radius: 12px;
    padding: 14px 18px;
    color: rgba(255, 255, 255, 0.92);

    @media (max-width: 640px) {
      flex-wrap: wrap;
    }
  }

  &__next-plan-icon {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: rgba(250, 183, 16, 0.18);
    color: #fab710;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9375rem;
  }

  &__next-plan-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__next-plan-eyebrow {
    font-size: 0.625rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    color: #fab710;
  }

  &__next-plan-text {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.92);
    line-height: 1.4;
  }

  &__next-plan-undo {
    flex-shrink: 0;
    appearance: none;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.16);
    color: #fff;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    transition: all 160ms ease;

    &:hover {
      background: rgba(255, 255, 255, 0.14);
      border-color: rgba(255, 255, 255, 0.28);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }

    i {
      font-size: 0.75rem;
    }
  }

  &__primary {
    background: $brand-color-primary !important;
    border-color: $brand-color-primary !important;
    color: #fff !important;
    font-weight: 600 !important;

    &:hover {
      background: #7aae26 !important;
      border-color: #7aae26 !important;
    }
  }

  &__secondary {
    color: rgba(255, 255, 255, 0.7) !important;

    &:hover {
      color: #fff !important;
      background: rgba(255, 255, 255, 0.08) !important;
    }
  }
}
</style>
